/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, OnInit, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConnectorRegistry } from './connector-registry.service';
import { MigrationApiService } from './migration-api.service';
import { MigrationService } from './migration.service';
import { MigrationConnection } from './migration.models';
import { ConnectionDialogComponent } from './connection-dialog.component';

/**
 * Saved connections. Add/edit/delete live in the repository document — the
 * parts that cannot honestly happen without the engine (test, and any secret
 * at all) are disabled until `MigrationApiService.available` says otherwise.
 */
@Component({
  selector: 'app-mig-connections',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  providers: [DatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './migration-connections.component.html'
})
export class MigrationConnectionsComponent implements OnInit {
  private readonly service = inject(MigrationService);
  private readonly dialog = inject(MatDialog);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);
  readonly registry = inject(ConnectorRegistry);
  readonly api = inject(MigrationApiService);

  readonly loading = signal(true);
  readonly connections = signal<MigrationConnection[]>([]);
  readonly busyId = signal('');

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.service.connections().subscribe((list) => {
      this.connections.set(list);
      this.loading.set(false);
    });
  }

  connectorName(id: string): string {
    const connector = this.registry.get(id);
    return connector ? this.translate.instant(connector.titleKey) : (id ?? '—');
  }

  connectorIcon(id: string): string {
    return this.registry.get(id)?.icon ?? 'cable';
  }

  /** A one-line non-secret summary — URL/host/endpoint, whichever exists. */
  targetOf(conn: MigrationConnection): string {
    const c = conn.config ?? {};
    return String(c['serverUrl'] ?? c['endpoint'] ?? c['host'] ?? c['path'] ?? c['siteUrl'] ?? '—');
  }

  add(): void {
    this.dialog
      .open(ConnectionDialogComponent, { width: '560px', maxWidth: '96vw', autoFocus: false })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.notification.showInfo(this.translate.instant('DMS.MIGRATION.CONN_SAVED'));
          this.load();
        }
      });
  }

  edit(conn: MigrationConnection): void {
    this.dialog
      .open(ConnectionDialogComponent, { width: '560px', maxWidth: '96vw', autoFocus: false, data: { connection: conn } })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.notification.showInfo(this.translate.instant('DMS.MIGRATION.CONN_SAVED'));
          this.load();
        }
      });
  }

  toggle(conn: MigrationConnection): void {
    this.busyId.set(conn.id);
    this.service.saveConnection({ ...conn, enabled: !conn.enabled }).subscribe({
      next: () => {
        this.busyId.set('');
        this.load();
      },
      error: () => {
        this.busyId.set('');
        this.notification.showError(this.translate.instant('DMS.MIGRATION.SAVE_FAIL'));
      }
    });
  }

  test(conn: MigrationConnection): void {
    if (!this.api.available()) {
      return;
    }
    this.busyId.set(conn.id);
    this.api.testConnection(conn.connectorId, conn.config, conn.id).subscribe({
      next: (res) => {
        this.busyId.set('');
        const key = res?.ok ? 'DMS.MIGRATION.TEST_OK' : 'DMS.MIGRATION.TEST_FAIL';
        this.notification.showInfo(this.translate.instant(key));
      },
      error: () => {
        this.busyId.set('');
        this.notification.showError(this.translate.instant('DMS.MIGRATION.TEST_FAIL'));
      }
    });
  }

  remove(conn: MigrationConnection): void {
    if (!confirm(this.translate.instant('DMS.MIGRATION.CONN_DELETE_CONFIRM', { name: conn.name }))) {
      return;
    }
    this.busyId.set(conn.id);
    if (this.api.available() === true) {
      this.api.forgetConnection(conn.id).subscribe({ error: () => undefined });
    }
    this.service.removeConnection(conn.id).subscribe({
      next: () => {
        this.busyId.set('');
        this.notification.showInfo(this.translate.instant('DMS.MIGRATION.CONN_DELETED'));
        this.load();
      },
      error: () => {
        this.busyId.set('');
        this.notification.showError(this.translate.instant('DMS.MIGRATION.SAVE_FAIL'));
      }
    });
  }

  dateLabel(iso?: string): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : (this.datePipe.transform(d, 'dd/MM/yyyy HH:mm') ?? '—');
  }

  trackById(_i: number, conn: MigrationConnection): string {
    return conn.id;
  }
}
